import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Phone, Lock, ArrowRight, AlertCircle, Building2 } from 'lucide-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { authAPI } from '../services/api';
import Input from '../modern-components/ui/Input';
import Button from '../modern-components/ui/Button';
import Card from '../modern-components/ui/Card';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const formik = useFormik({
    initialValues: {
      mobile: '',
      password: ''
    },
    validationSchema: Yup.object({
      mobile: Yup.string()
        .matches(/^[0-9]{10}$/, 'Mobile number must be 10 digits')
        .required('Mobile number is required'),
      password: Yup.string()
        .min(6, 'Password must be at least 6 characters')
        .required('Password is required')
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError('');

      try {
        const response = await authAPI.login(values.mobile, values.password);
        if (response.data.success) {
          const userData = response.data.data.user;

          console.log('Login response:', response.data); // Debug
          console.log('User data from login:', userData); // Debug
          console.log('User type:', userData.user_type); // Debug

          // Check if user is admin or supplier
          if (userData.user_type === 'admin' || userData.user_type === 'supplier') {
            localStorage.setItem('token', response.data.data.token);
            localStorage.setItem('user', JSON.stringify(userData));

            console.log('Saved to localStorage:', localStorage.getItem('user')); // Debug

            // Redirect to admin dashboard
            navigate('/admin/dashboard', { replace: true });
          } else {
            setError('This login is for administrators and suppliers only');
          }
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Invalid credentials');
      } finally {
        setLoading(false);
      }
    }
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-700 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-2xl shadow-lg mb-4">
            <Shield className="w-10 h-10 text-primary-600" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">
            Admin Portal
          </h1>
          <p className="text-primary-100">
            Manage your marketplace operations
          </p>
        </div>

        {/* Login Card */}
        <Card className="animate-slide-up shadow-2xl">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Sign In
            </h2>
            <p className="text-gray-600">
              Enter your credentials to access the admin panel
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={formik.handleSubmit} className="space-y-5">
            {/* Mobile Number */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Mobile Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  name="mobile"
                  placeholder="Enter 10-digit mobile number"
                  value={formik.values.mobile}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={loading}
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                    formik.touched.mobile && formik.errors.mobile
                      ? 'border-red-300'
                      : 'border-gray-300'
                  }`}
                />
              </div>
              {formik.touched.mobile && formik.errors.mobile && (
                <p className="mt-1 text-sm text-red-600">{formik.errors.mobile}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="password"
                  name="password"
                  placeholder="Enter your password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={loading}
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                    formik.touched.password && formik.errors.password
                      ? 'border-red-300'
                      : 'border-gray-300'
                  }`}
                />
              </div>
              {formik.touched.password && formik.errors.password && (
                <p className="mt-1 text-sm text-red-600">{formik.errors.password}</p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              disabled={loading || !formik.isValid}
              className="w-full"
              rightIcon={!loading && <ArrowRight className="w-5 h-5" />}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Signing in...
                </>
              ) : (
                'Sign In to Dashboard'
              )}
            </Button>
          </form>

          {/* Footer Links */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <button
                onClick={() => navigate('/login')}
                className="text-primary-600 hover:text-primary-700 font-semibold"
              >
                ← Back to Customer Login
              </button>
            </div>
          </div>
        </Card>

        {/* Info Box */}
        <div className="mt-6 text-center">
          <p className="text-primary-100 text-sm">
            For customer access, use the{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-white font-semibold hover:underline"
            >
              customer login page
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
